import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Star, Users, ArrowLeft } from 'lucide-react';
import { businessApi } from '../services/api';

const BusinessCard = ({ business }) => (
  <Link
    to={`/businesses/${business.slug || business.id}`}
    className={`business-card hover-lift ${business.is_featured ? 'featured' : ''}`}
    data-testid={`minority-business-${business.id}`}
  >
    <div className="business-card-image flex items-center justify-center text-neutral-900 text-4xl font-bold">
      {business.photos?.[0] ? (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${business.photos[0]}`}
          alt={business.business_name}
          className="w-full h-full object-contain"
        />
      ) : (
        business.business_name.charAt(0)
      )}
    </div>
    <div className="business-card-content">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-bold text-lg text-neutral-900">{business.business_name}</h3>
        {business.is_featured && (
          <span className="badge badge-featured flex items-center gap-1 flex-shrink-0">
            <Star className="w-3 h-3" /> Featured
          </span>
        )}
      </div>
      <p className="text-sm text-neutral-500 mb-2">{business.primary_category}</p>
      <p className="text-sm text-neutral-600 line-clamp-2 mb-3">{business.description}</p>
      {business.city_state_zip && (
        <div className="flex items-center gap-1 text-sm text-neutral-500 mb-3">
          <MapPin className="w-4 h-4" />
          <span className="line-clamp-1">{business.city_state_zip}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {business.ownership_tags?.map(tag => (
          <span key={tag} className="badge badge-minority text-xs">{tag}</span>
        ))}
      </div>
    </div>
  </Link>
);

const MinorityOwned = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      const response = await businessApi.getMinorityOwned();
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error loading businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const { featured, categoryGroups } = useMemo(() => {
    const feat = [];
    const grouped = {};
    businesses.forEach((biz) => {
      if (biz.is_featured) feat.push(biz);
      const cat = biz.primary_category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(biz);
    });
    const sortedCats = Object.keys(grouped).sort().map((cat) => ({
      category: cat,
      businesses: grouped[cat].filter(b => !b.is_featured)
        .sort((a, b) => a.business_name.localeCompare(b.business_name)),
    }));
    return { featured: feat, categoryGroups: sortedCats };
  }, [businesses]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white" data-testid="minority-owned-page">
      <Helmet>
        <title>Minority & Woman-Owned Businesses in Martinsville & Henry County, VA | Explore MHC</title>
        <meta name="description" content="Support minority-owned, woman-owned, and veteran-owned businesses in Martinsville and Henry County, Virginia. Browse local diverse businesses making an impact." />
        <link rel="canonical" href="https://exploremhc.com/minority-owned" />
        <meta property="og:title" content="Minority & Woman-Owned Businesses in Martinsville & Henry County, VA" />
        <meta property="og:description" content="Support minority-owned, woman-owned, and veteran-owned businesses in Martinsville and Henry County, Virginia." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1765648763990-ad0ee9b546c9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200&h=630&fit=crop" />
      </Helmet>
      <div className="bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/businesses" className="inline-flex items-center gap-2 text-neutral-600 hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Directory
          </Link>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center flex-shrink-0">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Minority-Owned Businesses
              </h1>
              <p className="text-lg text-neutral-600">
                Supporting diverse businesses in Martinsville and Henry County
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <p className="text-neutral-600">
            {loading ? 'Loading...' : `${businesses.length} minority-owned business${businesses.length !== 1 ? 'es' : ''}`}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : businesses.length > 0 ? (
          <div className="space-y-12">
            {featured.length > 0 && (
              <div data-testid="featured-section">
                <div className="flex items-center gap-2 mb-5">
                  <Star className="w-5 h-5 text-accent fill-accent" />
                  <h2 className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    Featured Businesses
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {featured.map(b => <BusinessCard key={`feat-${b.id}`} business={b} />)}
                </div>
              </div>
            )}

            {categoryGroups.map(({ category, businesses: catBizs }) => {
              if (catBizs.length === 0) return null;
              return (
                <div key={category} data-testid={`category-group-${category}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <h2 className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
                      {category}
                    </h2>
                    <span className="text-sm text-neutral-500 bg-neutral-100 px-2.5 py-0.5 rounded-full">
                      {catBizs.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {catBizs.map(b => <BusinessCard key={b.id} business={b} />)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <h3 className="text-xl font-bold text-neutral-900 mb-2">No businesses yet</h3>
            <p className="mb-4">Be the first to list your minority-owned business!</p>
            <Link to="/register" className="btn-primary">
              Register Your Business
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MinorityOwned;
